import Header from '@components/shared/Header';
import { useTranslation } from 'react-i18next';
import { Button, Col, Row, Typography } from 'antd';
import V2Button from '@components/shared/V2Button';
import { isEmpty } from 'lodash';
import cs from 'classnames';
import DataPoint from '@components/layouts/Truva/Listing/AboutBuilding/DataPoint';
import ExpandableDescription from '@components/shared/ExpandableDescription';
import routes from '@root/routes';
import type { BuildingInListing, SpaceProvider } from '@root/types';
import darkContentCss from '@styles/darkContentSection.module.less';
import s from './AboutBuilding.module.less';

const { Title } = Typography;

type Props = {
  building: BuildingInListing;
  viewBuildingClicked: () => void;
  spaceProvider: SpaceProvider | null;
};

const AboutBuilding = ({ building, viewBuildingClicked, spaceProvider }: Props) => {
  const { t } = useTranslation('listing');

  return (
    <div className={cs(s.row, darkContentCss.darkContentSection)}>
      <div>
        <div className={cs(s.aboutHeaderRow)}>
          <Header>{t('aboutBuilding.title')}</Header>
          <a href={routes.building(building.slug)}>
            <V2Button
              className={s.viewBuildingButton}
              type="tertiary"
              size="medium"
              onClick={() => {
                viewBuildingClicked();
              }}
            >
              {t('aboutBuilding.viewBuilding')}
            </V2Button>
          </a>
          <Button
            className={s.viewBuildingMobileButton}
            onClick={() => {
              viewBuildingClicked();
            }}
            href={routes.building(building.slug)}
          >
            {t('aboutBuilding.viewBuildingShort')}
          </Button>
        </div>
        <Row gutter={16}>
          {building.photo ? (
            <Col lg={8} md={12} sm={24}>
              <Row>
                <Col>
                  <div className={s.heroImageContainer}>
                    <a href={routes.building(building.slug)}>
                      <img
                        className={s.heroImage}
                        src={building.photo.mediumPath}
                        alt={building.photo.description}
                      />
                    </a>
                  </div>
                </Col>
              </Row>
            </Col>
          ) : null}
          <Col lg={building.photo ? 16 : 24} md={building.photo ? 12 : 24} sm={24}>
            <Row className={s.infoSection}>
              <Col>
                <Title level={4} className={s.buildingName}>
                  {building.primaryName}
                </Title>
              </Col>
            </Row>
            <Row className={s.infoSection}>
              <Col>
                <ExpandableDescription>{building.description}</ExpandableDescription>
              </Col>
            </Row>
            {isEmpty(building.notableTenants) ? null : (
              <p className={s.notableTenants}>
                <strong className={s.notableTenantsText}>
                  {t('aboutBuilding.notableTenants')}
                </strong>
                {building.notableTenants.join(', ')}
              </p>
            )}
            <Row>
              <DataPoint
                label={t('aboutBuilding.numberOfFloors')}
                value={building.numberOfFloors}
              />
              <DataPoint label={t('aboutBuilding.yearRenovated')} value={building.yearRenovated} />
              <DataPoint label={t('aboutBuilding.yearBuilt')} value={building.yearBuilt} />
              <DataPoint
                label={t('aboutBuilding.rentableArea')}
                value={
                  building.rentableArea ? t('units:area', { area: building.rentableArea }) : null
                }
              />
              <DataPoint
                label={t('aboutBuilding.buildingOwner')}
                value={building.owningCompany}
                href={spaceProvider?.slug ? routes.landlords(spaceProvider.slug) : null}
              />
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AboutBuilding;
